import { useLocation } from "react-router-dom";

export type SearchParameters = {
    readonly cloudId: string;
    readonly requestId: string;
    readonly products: readonly string[];
    readonly referrer?: string;
    readonly onDemandUrl?: string;
    readonly siteRenameRequestId?: string;
    readonly isClaiming?: boolean;
    readonly locale?: string;
    readonly provisioningRequestId?: string;

    // tintin
    readonly platform: "ccp" | "hams" | "routing";
    readonly provisionRequestId?: string;

    // for redirect to instance
    readonly dataRegion?: string;
    readonly admin?: boolean;

    // for tracking
    readonly googleappsintegration?: string;
    readonly googleappsdomain?: string;

    // for uknown reasons
    readonly lasso?: string;
    // devops bundle land in code
    readonly signupSource?: string;
    readonly isFoundingAdmin?: string;

    // redirect to WAC App Provisioning
    readonly provisionApp?: string;

    // hide user segmentation screen
    readonly hideUserSegmentation?: string;

    // appKey for installing 2p app after provisioning on confirmation page
    readonly appKey?: string;

    // auto-generated site name
    readonly templateId?: string;
    readonly edition?: string;

    // Project Cobra (Arcadia Platform Cut Sign Up Steps in Half)
    readonly asp?: string;

    // determines if this signup is a n2n user
    readonly n2n: boolean;

    // maintains template selection on refresh (confirmation page)
    readonly templateChoice?: string;

    // GraphQL experiment
    readonly orchestrationId?: string;
};

/**
 * @returns {URLSearchParams} The search (query) parameters from the URL.
 *
 * This function is used to get the search parameters from the URL.
 * If in a router context, it uses the {@link useLocation} hook,
 * otherwise it uses the global location.
 */
const getSearchParameters = (): URLSearchParams => {
    try {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        return new URLSearchParams(useLocation().search);
    } catch {
        return new URLSearchParams(location.search);
    }
};

// eslint-disable-next-line complexity
export function useSearchParameters(): SearchParameters {
    const searchParameters = getSearchParameters();
    const cloudId = searchParameters.get("cloudId") ?? "";
    const requestId = searchParameters.get("requestId") ?? "";
    const products = searchParameters.get("products") ?? "";
    const referrer = searchParameters.get("referrer") ?? undefined;
    const isClaiming = searchParameters.get("claim") === "true";
    const dataRegion = searchParameters.get("dataRegion") ?? undefined;
    const onDemandUrl = searchParameters.get("ondemandurl") ?? undefined;
    const admin = searchParameters.get("admin") === "true";
    const siteRenameRequestId =
        searchParameters.get("siteRenameRequestId") ?? undefined;
    const locale = searchParameters.get("locale") ?? "";
    const platformParam = searchParameters.get("platform");
    const platform =
        platformParam === "ccp" || platformParam === "hams"
            ? platformParam
            : "routing";
    const provisioningRequestId =
        searchParameters.get("provisioningRequestId") ?? undefined;
    const signupSource = searchParameters.get("signupSource") ?? undefined;
    const isFoundingAdmin =
        searchParameters.get("isFoundingAdmin") ?? undefined;
    const provisionApp = searchParameters.get("provisionApp") ?? undefined;
    const hideUserSegmentation =
        searchParameters.get("hideUserSegmentation") ?? undefined;
    const appKey = searchParameters.get("appKey") ?? undefined;
    const templateId = searchParameters.get("templateId") ?? undefined;
    const templateChoice = searchParameters.get("templateChoice") ?? undefined;
    const edition = searchParameters.get("edition") ?? undefined;
    const asp = searchParameters.get("asp") ?? "";
    const n2n = searchParameters.get("n2n") === "true";
    const orchestrationId =
        searchParameters.get("orchestrationId") ?? undefined;

    return {
        cloudId,
        requestId,
        products: products.split(","),
        referrer,
        isClaiming,
        onDemandUrl,
        dataRegion,
        siteRenameRequestId,
        admin,
        locale,
        platform,
        provisioningRequestId,
        signupSource,
        isFoundingAdmin,
        provisionApp,
        hideUserSegmentation,
        appKey,
        templateId,
        templateChoice,
        edition,
        asp,
        n2n,
        orchestrationId
    };
}
